export const normalizeMaybeId = (value) => {
  if (value === '' || value == null) return null;
  return String(value).trim() || null;
};

export const buildArtworkProofMatchKey = ({ artworkId = null, orderItemId = null, decorationId = null } = {}) =>
  [normalizeMaybeId(artworkId) || '', normalizeMaybeId(orderItemId) || '', normalizeMaybeId(decorationId) || ''].join('::');

export const resolveArtworkIdentity = (artwork = {}, product = {}, decoration = {}) => ({
  artworkId: normalizeMaybeId(
    artwork?.artworkId ??
      artwork?.ArtworkId ??
      artwork?.id ??
      artwork?.Id ??
      decoration?.ArtWork?.id ??
      decoration?.ArtWork?.Id
  ),
  orderItemId: normalizeMaybeId(
    artwork?.orderItemId ??
      artwork?.OrderItemId ??
      decoration?.orderItemId ??
      decoration?.OrderItemId ??
      product?.orderItemId ??
      product?.OrderItemId ??
      product?.orderProductId ??
      product?.source?.orderItemId ??
      product?.id ??
      product?.Id
  ),
  decorationId: normalizeMaybeId(
    artwork?.decorationId ??
      artwork?.DecorationId ??
      decoration?.decorationId ??
      decoration?.DecorationId ??
      decoration?.id ??
      decoration?.Id
  ),
});

export const resolveProofBundleRowIdentity = (row = {}) =>
  resolveArtworkIdentity(
    {
      artworkId:
        row?.mapping?.artworkId ??
        row?.proof?.artworkId ??
        row?.artwork?.artworkId ??
        row?.artwork?.ArtworkId ??
        row?.artwork?.id ??
        row?.artwork?.Id,
      orderItemId:
        row?.mapping?.orderItemId ??
        row?.proof?.orderItemId ??
        row?.artwork?.orderItemId ??
        row?.artwork?.OrderItemId,
      decorationId:
        row?.mapping?.decorationId ??
        row?.proof?.decorationId ??
        row?.artwork?.decorationId ??
        row?.artwork?.DecorationId,
      ...row?.artwork,
    },
    {
      orderItemId:
        row?.mapping?.orderItemId ??
        row?.proof?.orderItemId ??
        row?.product?.orderItemId ??
        row?.product?.OrderItemId,
      id:
        row?.mapping?.orderItemId ??
        row?.proof?.orderItemId ??
        row?.product?.id ??
        row?.product?.Id,
      ...row?.product,
    },
    {
      decorationId:
        row?.mapping?.decorationId ??
        row?.proof?.decorationId ??
        row?.decoration?.decorationId ??
        row?.decoration?.DecorationId,
      id:
        row?.mapping?.decorationId ??
        row?.proof?.decorationId ??
        row?.decoration?.id ??
        row?.decoration?.Id,
      ...row?.decoration,
    }
  );

export const buildProofReplacementLookup = (rows = []) => {
  const exact = new Map();
  const byArtworkId = new Map();
  const byOrderItemDecoration = new Map();
  const scopedCompletePackage = [];

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const artworkIdentity = resolveProofBundleRowIdentity(row);
    const replacement = {
      mappingId: row?.mapping?.id ?? null,
      sourceType: row?.mapping?.sourceType ?? row?.proof?.sourceType ?? null,
      sourceProvider: row?.mapping?.sourceProvider ?? row?.proof?.sourceProvider ?? null,
      proofRow: row?.proof || null,
      mapping: row?.mapping || null,
    };
    const exactKey = buildArtworkProofMatchKey(artworkIdentity);
    if (exactKey !== '::' && !exact.has(exactKey)) {
      exact.set(exactKey, replacement);
    }
    if (artworkIdentity.artworkId && !byArtworkId.has(artworkIdentity.artworkId)) {
      byArtworkId.set(artworkIdentity.artworkId, replacement);
    }
    const itemDecorationKey = buildArtworkProofMatchKey({
      orderItemId: artworkIdentity.orderItemId,
      decorationId: artworkIdentity.decorationId,
    });
    if ((artworkIdentity.orderItemId || artworkIdentity.decorationId) && !byOrderItemDecoration.has(itemDecorationKey)) {
      byOrderItemDecoration.set(itemDecorationKey, replacement);
    }

    const approvalMode = String(row?.mapping?.approvalMode || '').trim().toLowerCase();
    const appliesTo = row?.mapping?.appliesTo;
    if (
      approvalMode === 'complete_package' &&
      appliesTo &&
      typeof appliesTo === 'object'
    ) {
      scopedCompletePackage.push({
        replacement,
        appliesTo,
      });
    }
  });

  return { exact, byArtworkId, byOrderItemDecoration, scopedCompletePackage };
};

export const attachProofReplacementsToProducts = (products = [], proofRows = []) => {
  const lookup = buildProofReplacementLookup(proofRows);
  return (Array.isArray(products) ? products : []).map((product) => {
    const hasLowercaseDecorations = Array.isArray(product?.decorations);
    const hasCapitalDecorations = Array.isArray(product?.Decorations);
    const decorations = hasLowercaseDecorations
      ? [...product.decorations]
      : hasCapitalDecorations
        ? [...product.Decorations]
        : [];
    const hasCapitalArtworks = Array.isArray(product?.ArtWorks);
    const hasLowercaseArtworks = Array.isArray(product?.artworks);
    const artworks = hasCapitalArtworks
      ? [...product.ArtWorks]
      : hasLowercaseArtworks
        ? [...product.artworks]
        : [];

    const nextArtworks = artworks.map((artwork, index) => {
      const decoration = decorations[index] || {};
      const identity = resolveArtworkIdentity(artwork, product, decoration);
      const exactKey = buildArtworkProofMatchKey(identity);
      const itemDecorationKey = buildArtworkProofMatchKey({
        orderItemId: identity.orderItemId,
        decorationId: identity.decorationId,
      });
      const scopedPackageReplacement =
        (lookup.scopedCompletePackage || [])
          .map((entry) => {
            const scope = String(entry?.appliesTo?.scope || '').trim().toLowerCase();
            const orderItemIds = new Set(
              (Array.isArray(entry?.appliesTo?.orderItemIds) ? entry.appliesTo.orderItemIds : [])
                .map(normalizeMaybeId)
                .filter(Boolean)
            );
            const decorationIds = new Set(
              (Array.isArray(entry?.appliesTo?.decorationIds) ? entry.appliesTo.decorationIds : [])
                .map(normalizeMaybeId)
                .filter(Boolean)
            );
            const artworkIds = new Set(
              (Array.isArray(entry?.appliesTo?.artworkIds) ? entry.appliesTo.artworkIds : [])
                .map(normalizeMaybeId)
                .filter(Boolean)
            );

            const artworkMatch = identity.artworkId && artworkIds.has(identity.artworkId);
            const decorationMatch = identity.decorationId && decorationIds.has(identity.decorationId);
            const productMatch = identity.orderItemId && orderItemIds.has(identity.orderItemId);

            if (scope === 'decorations') {
              if (!decorationMatch && !artworkMatch) return null;
              if (orderItemIds.size && !productMatch) return null;
              return entry.replacement;
            }
            if (scope === 'products') {
              return productMatch ? entry.replacement : null;
            }
            if (scope === 'order') {
              return entry.replacement;
            }
            return artworkMatch || decorationMatch || productMatch ? entry.replacement : null;
          })
          .find(Boolean) || null;

      const proofReplacement =
        lookup.exact.get(exactKey) ||
        (identity.artworkId ? lookup.byArtworkId.get(identity.artworkId) : null) ||
        lookup.byOrderItemDecoration.get(itemDecorationKey) ||
        scopedPackageReplacement ||
        null;

      return proofReplacement ? { ...artwork, proofReplacement } : { ...artwork };
    });

    return {
      ...product,
      ...(hasLowercaseDecorations ? { decorations } : {}),
      ...(hasCapitalDecorations ? { Decorations: decorations } : {}),
      ArtWorks: nextArtworks,
      artworks: nextArtworks,
    };
  });
};
